import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { deepCopy } from 'na-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isMissingMetaValue, SchemaFieldMeta, SchemaMetaFieldType, SchemaMetaTemplate, SchemaMetaTemplateField } from '../schema-meta.model';

@Component({
  selector: 'app-schema-meta-form',
  templateUrl: './schema-meta-form.component.html',
  styleUrls: ['./schema-meta-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslatePipe
  ]
})
export class SchemaMetaFormComponent implements OnChanges {
  constructor(private translate: TranslateService) {}
  @Input() metaTemplate: SchemaMetaTemplate | undefined;
  @Input() fieldMeta: SchemaFieldMeta | undefined;
  @Input() showTemplateEditor = true;
  @Input() showFieldValues = true;
  @Input() showValuesHeading = true;
  @Input() showValidationErrors = false;
  @Output() metaTemplateChange = new EventEmitter<SchemaMetaTemplate>();
  @Output() fieldMetaChange = new EventEmitter<SchemaFieldMeta | undefined>();

  template: SchemaMetaTemplate = { fields: [] };
  meta: SchemaFieldMeta = {};
  readonly types: Array<{ value: SchemaMetaFieldType; labelKey: string }> = [
    { value: 'string', labelKey: 'contentTypes.text' },
    { value: 'textarea', labelKey: 'contentTypes.metaMultilineText' },
    { value: 'number', labelKey: 'contentTypes.number' },
    { value: 'boolean', labelKey: 'contentTypes.metaYesNo' },
    { value: 'date', labelKey: 'contentTypes.date' }
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metaTemplate']) this.template = this.normalizeTemplate(this.metaTemplate);
    if (changes['fieldMeta']) this.meta = deepCopy(this.fieldMeta || {});
  }

  addTemplateField(): void {
    const index = this.template.fields.length + 1;
    const usedKeys = new Set(this.template.fields.map(field => field.key));
    this.template = {
      fields: [...this.template.fields, {
        key: this.getUniqueKey(`metaField${index}`, usedKeys),
        displayName: `${this.translate.instant('contentTypes.metaField')} ${index}`,
        type: 'string',
        mandatory: false
      }]
    };
    this.emitTemplate();
  }

  updateTemplateField(index: number, field: SchemaMetaTemplateField): void {
    const fields = [...this.template.fields];
    fields[index] = { ...field };
    this.template = { fields };
    this.emitTemplate();
  }

  isTemplateKeyDuplicate(index: number): boolean {
    const key = this.template.fields[index]?.key?.trim();
    return !!key && this.template.fields.some((field, fieldIndex) =>
      fieldIndex !== index && field.key?.trim() === key);
  }

  removeTemplateField(index: number): void {
    this.template = { fields: this.template.fields.filter((_, fieldIndex) => fieldIndex !== index) };
    this.emitTemplate();
  }

  updateMetaValue(field: SchemaMetaTemplateField, value: unknown): void {
    const meta = { ...this.meta };
    if (value === '' || value === undefined || value === null) delete meta[field.key];
    else meta[field.key] = value;
    this.meta = meta;
    this.fieldMetaChange.emit(Object.keys(meta).length ? meta : undefined);
  }

  isRequiredValueMissing(field: SchemaMetaTemplateField): boolean {
    return Boolean(field.mandatory && isMissingMetaValue(this.meta[field.key]));
  }

  private emitTemplate(): void {
    this.metaTemplateChange.emit(deepCopy(this.template));
  }

  private normalizeTemplate(template: SchemaMetaTemplate | undefined): SchemaMetaTemplate {
    return { fields: deepCopy(template?.fields || []).map(field => ({
      key: field.key ?? '',
      displayName: field.displayName ?? '',
      description: field.description ?? '',
      mandatory: Boolean(field.mandatory),
      type: this.types.some(type => type.value === field.type) ? field.type : 'string'
    })) };
  }

  private getUniqueKey(baseKey: string, usedKeys: Set<string>): string {
    let key = baseKey;
    let suffix = 2;
    while (usedKeys.has(key)) key = `${baseKey}${suffix++}`;
    usedKeys.add(key);
    return key;
  }
}
